// Offline state management service
// Handles network detection, cache status tracking, and pending operations

import { NetworkStatus } from '../types/index.js';
import { cacheDebugLog } from '../utils/cache-debug.js';

class OfflineStateService {
  constructor() {
    this.listeners = new Set();
    this.isOnline = navigator.onLine;
    this.lastOnlineTime = Date.now();
    this.lastOfflineTime = null;
    this.reconnectAttempts = 0;
    this.pendingOperations = 0;
    this._lastOfflineReload = 0;
    this.consecutiveFailures = 0;
    this.DEGRADED_THRESHOLD = 2;
    this.isDegraded = false;
    
    this.initialize();
  }

  /**
   * Initialize network event listeners
   */
  initialize() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline.bind(this));
      window.addEventListener('offline', this.handleOffline.bind(this));
    }
  }

  /**
   * Handle online event
   */
  handleOnline() {
    this.isOnline = true;
    this.lastOnlineTime = Date.now();
    this.reconnectAttempts = 0;
    
    console.log('[OfflineState] Connection restored');
    cacheDebugLog('network-online', { lastOfflineTime: this.lastOfflineTime });
    this.notifyListeners();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
    this.lastOfflineTime = Date.now();
    
    console.log('[OfflineState] Connection lost');
    cacheDebugLog('network-offline', {});
    this.notifyListeners();

    const now = Date.now();
    if (typeof window !== 'undefined' && now - this._lastOfflineReload > 30000) {
      this._lastOfflineReload = now;
      setTimeout(() => window.location.reload(), 500);
    }
  }

  /**
   * Get current network status
   */
  getStatus() {
    if (this.isDegraded) return NetworkStatus.DEGRADED;
    return this.isOnline ? NetworkStatus.ONLINE : NetworkStatus.OFFLINE;
  }

  /**
   * Check if currently online and the backend is reachable.
   * Returns false when DEGRADED so consumers (sync engine,
   * offline API service) transparently fall back to offline
   * behaviour without needing to check a separate flag.
   */
  isCurrentlyOnline() {
    return this.isOnline && !this.isDegraded;
  }

  /**
   * Check if currently offline per the OS link layer.
   * Unlike isCurrentlyOnline, this is NOT affected by DEGRADED
   * so shouldQueueMutationsNow still routes to the network
   * path (which then returns WEAK_CONNECTION after the timeout).
   */
  isCurrentlyOffline() {
    return !this.isOnline;
  }

  /**
   * Check if the backend is unreachable while the OS claims to be
   * online — Wi-Fi with no Internet, VPN blocking, DNS failure, etc.
   */
  isCurrentlyDegraded() {
    return this.isOnline && this.isDegraded;
  }

  /**
   * Align internal flag with navigator when the browser reports offline.
   * Avoids missed "offline" events so mutation queue path runs instead of hanging fetch().
   */
  syncFromNavigator() {
    if (typeof navigator === 'undefined') return;
    if (!navigator.onLine && this.isOnline) {
      this.handleOffline();
    } else if (navigator.onLine && !this.isOnline) {
      this.handleOnline();
    }
  }

  /**
   * Record a failed request. When the backend stays unreachable despite
   * navigator.onLine being true (Wi-Fi no Internet, VPN blocking, DNS
   * failure), transition to DEGRADED so the sync engine pauses and the
   * API wrapper returns WEAK_CONNECTION instead of queuing mutations.
   */
  markNetworkFailure() {
    this.consecutiveFailures++;
    cacheDebugLog('network-request-failed', {
      navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      consecutiveFailures: this.consecutiveFailures,
      threshold: this.DEGRADED_THRESHOLD,
    });

    if (
      !this.isDegraded &&
      typeof navigator !== 'undefined' &&
      navigator.onLine &&
      this.consecutiveFailures >= this.DEGRADED_THRESHOLD
    ) {
      this.isDegraded = true;
      console.log('[OfflineState] Connection degraded — backend unreachable despite online status');
      this.notifyListeners();
    }
  }

  /**
   * Restore normal mode after a successful request.
   * Resets the consecutive-failure counter, recovers from DEGRADED
   * when the backend is reachable again, and fires handleOnline
   * if the browser has already reported online (missed event).
   */
  markNetworkSuccess() {
    this.consecutiveFailures = 0;

    if (this.isDegraded) {
      this.isDegraded = false;
      console.log('[OfflineState] Connection recovered — backend reachable again');
      this.notifyListeners();
    }

    if (
      typeof navigator !== 'undefined' &&
      navigator.onLine &&
      !this.isOnline
    ) {
      this.handleOnline();
    }
  }

  /**
   * Get time since last online
   */
  getTimeSinceLastOnline() {
    return Date.now() - this.lastOnlineTime;
  }

  /**
   * Get time since last offline
   */
  getTimeSinceLastOffline() {
    if (!this.lastOfflineTime) return null;
    return Date.now() - this.lastOfflineTime;
  }

  /**
   * Increment pending operations counter
   */
  incrementPendingOperations() {
    this.pendingOperations++;
    this.notifyListeners();
  }

  /**
   * Decrement pending operations counter
   */
  decrementPendingOperations() {
    this.pendingOperations = Math.max(0, this.pendingOperations - 1);
    this.notifyListeners();
  }

  /**
   * Get pending operations count
   */
  getPendingOperationsCount() {
    return this.pendingOperations;
  }

  /**
   * Check if there are pending operations
   */
  hasPendingOperations() {
    return this.pendingOperations > 0;
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener) {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state change.
   * Handles both sync and async listeners; a single failing listener
   * never breaks the rest.
   */
  notifyListeners() {
    const state = this.getState();
    for (const listener of this.listeners) {
      try {
        const result = listener(state);
        if (result && typeof result.then === 'function') {
          result.catch(error =>
            console.error('[OfflineState] Async listener error:', error)
          );
        }
      } catch (error) {
        console.error('[OfflineState] Listener error:', error);
      }
    }
  }

  /**
   * Get complete state.
   * isOnline reflects effective reachability (accounts for DEGRADED),
   * status returns the three-state quality, and degradation counters
   * are exposed so subscribers can track transitions.
   */
  getState() {
    return {
      isOnline: this.isCurrentlyOnline(),
      status: this.getStatus(),
      lastOnlineTime: this.lastOnlineTime,
      lastOfflineTime: this.lastOfflineTime,
      timeSinceLastOnline: this.getTimeSinceLastOnline(),
      timeSinceLastOffline: this.getTimeSinceLastOffline(),
      pendingOperations: this.pendingOperations,
      reconnectAttempts: this.reconnectAttempts,
      isDegraded: this.isDegraded,
      consecutiveFailures: this.consecutiveFailures,
    };
  }

  /**
   * Manually trigger reconnection check
   */
  async checkConnection() {
    try {
      // Try to fetch a small resource
      const response = await fetch(window.location.href, {
        method: 'HEAD',
        cache: 'no-store',
      });
      
      if (response.ok) {
        if (!this.isOnline) {
          this.handleOnline();
        }
        return true;
      }
      
      return false;
    } catch (error) {
      if (this.isOnline) {
        this.handleOffline();
      }
      return false;
    }
  }

  /**
   * Increment reconnect attempts
   */
  incrementReconnectAttempts() {
    this.reconnectAttempts++;
  }

  /**
   * Reset reconnect attempts
   */
  resetReconnectAttempts() {
    this.reconnectAttempts = 0;
  }

  /**
   * Get reconnect delay with exponential backoff
   */
  getReconnectDelay() {
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, this.reconnectAttempts), maxDelay);
    return delay;
  }

  /**
   * Cleanup
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline.bind(this));
      window.removeEventListener('offline', this.handleOffline.bind(this));
    }
    this.listeners.clear();
  }
}

// Export singleton instance
export const offlineStateService = new OfflineStateService();
